package com.evoapps.evoshift.ui.workplaces

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.widgets.rememberStableText
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.adaptive.FormMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.legal.WorkplaceDpaNote
import com.evoapps.evoshift.ui.util.asString
import evoshift.composeapp.generated.resources.availability_slot_all_day
import evoshift.composeapp.generated.resources.create_workplace_business_hint
import evoshift.composeapp.generated.resources.create_workplace_business_name
import evoshift.composeapp.generated.resources.create_workplace_name_hint
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Business
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Store
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.GoldMetallicBrush
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.billing.PlanLimitDialog
import com.evoapps.evoshift.viewmodels.CreateWorkplaceEvent
import com.evoapps.evoshift.viewmodels.WorkplaceCreationViewModel
import com.evoapps.evoshift.widgets.StandoutCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.create_workplace_button
import evoshift.composeapp.generated.resources.create_workplace_description
import evoshift.composeapp.generated.resources.create_workplace_label
import evoshift.composeapp.generated.resources.create_workplace_title
import org.jetbrains.compose.resources.stringResource

@Composable
fun CreateWorkplaceScreen(
    viewModel: WorkplaceCreationViewModel,
    onNavigateToDashboard: () -> Unit,
    onSeePlans: () -> Unit = {}
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val allDaySlotLabel = stringResource(Res.string.availability_slot_all_day)
    val snackbarHostState = remember { SnackbarHostState() }

    state.planLimit?.let { limit ->
        PlanLimitDialog(
            limit = limit,
            onSeePlans = {
                viewModel.onEvent(CreateWorkplaceEvent.PlanLimitShown)
                onSeePlans()
            },
            onDismiss = { viewModel.onEvent(CreateWorkplaceEvent.PlanLimitShown) }
        )
    }

    // 1. Handle Navigation Side Effect
    LaunchedEffect(state.isCreated) {
        if (state.isCreated) {
            onNavigateToDashboard()
        }
    }

    // 2. Handle Error Messages
    val errorText = state.error?.asString()
    LaunchedEffect(errorText) {
        errorText?.let { error ->
            snackbarHostState.showSnackbar(error)
            viewModel.onEvent(CreateWorkplaceEvent.ErrorShown)
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        containerColor = Color.Transparent
    ) { padding ->
        com.evoapps.evoshift.widgets.GradientBox(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding),
            contentAlignment = Alignment.Center
        ) {
            if (state.isCheckingOwner) {
                CircularProgressIndicator(color = LightGold)
            } else {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState())
                        .contentWidth(FormMaxWidth)
                        .padding(24.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    // --- Header ---
                    Icon(
                        imageVector = Icons.Default.Business,
                        contentDescription = null,
                        modifier = Modifier.size(64.dp),
                        tint = LightGold
                    )

                    Spacer(modifier = Modifier.height(24.dp))

                    Text(
                        text = stringResource(Res.string.create_workplace_title),
                        style = MaterialTheme.typography.headlineMedium,
                        fontWeight = FontWeight.Bold,
                        color = LightGold,
                        textAlign = TextAlign.Center
                    )

                    Spacer(modifier = Modifier.height(8.dp))

                    Text(
                        text = stringResource(Res.string.create_workplace_description),
                        style = MaterialTheme.typography.bodyMedium,
                        color = Color.White.copy(alpha = 0.7f),
                        textAlign = TextAlign.Center
                    )

                    Spacer(modifier = Modifier.height(32.dp))

                    StandoutCard(
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Column(
                            modifier = Modifier.padding(24.dp),
                            verticalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            if (!state.hasExistingOwnerProfile) {
                                // --- Business Name Input Field ---
                                WorkplaceTextField(
                                    value = state.businessName,
                                    onValueChange = { viewModel.onEvent(CreateWorkplaceEvent.UpdateBusinessName(it)) },
                                    label = stringResource(Res.string.create_workplace_business_name),
                                    placeholder = stringResource(Res.string.create_workplace_business_hint),
                                    icon = Icons.Default.Business,
                                    imeAction = ImeAction.Next
                                )
                            }

                            // --- Workplace Input Field ---
                            WorkplaceTextField(
                                value = state.workplaceName,
                                onValueChange = { viewModel.onEvent(CreateWorkplaceEvent.UpdateName(it)) },
                                label = stringResource(Res.string.create_workplace_label),
                                placeholder = stringResource(Res.string.create_workplace_name_hint),
                                icon = Icons.Default.Store,
                                imeAction = ImeAction.Done,
                                onDone = { viewModel.onEvent(CreateWorkplaceEvent.Submit(allDaySlotLabel)) }
                            )

                            Spacer(modifier = Modifier.height(8.dp))

                            // --- Submit Button ---
                            val isSubmitEnabled = !state.isLoading && state.workplaceName.isNotBlank() && (state.hasExistingOwnerProfile || state.businessName.isNotBlank())
                            
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(56.dp)
                                    .clip(RoundedCornerShape(12.dp))
                                    .background(
                                        brush = GoldMetallicBrush,
                                        alpha = if (isSubmitEnabled) 1f else 0.5f
                                    )
                                    .handCursor().clickable(enabled = isSubmitEnabled) {
                                        viewModel.onEvent(CreateWorkplaceEvent.Submit(allDaySlotLabel))
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                if (state.isLoading) {
                                    CircularProgressIndicator(
                                        modifier = Modifier.size(24.dp),
                                        color = DeepNavy,
                                        strokeWidth = 2.dp
                                    )
                                } else {
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Icon(Icons.Default.Check, contentDescription = null, tint = DeepNavy)
                                        Spacer(Modifier.width(8.dp))
                                        Text(
                                            stringResource(Res.string.create_workplace_button),
                                            color = DeepNavy,
                                            fontWeight = FontWeight.Bold,
                                            fontSize = 16.sp
                                        )
                                    }
                                }
                            }
                            // Opening a workplace accepts the data processing annex (SH-50).
                            WorkplaceDpaNote(color = com.evoapps.evoshift.widgets.LocalOnColours.current.onSurfaceVariant)
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun WorkplaceTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    placeholder: String,
    icon: ImageVector,
    imeAction: ImeAction,
    onDone: (() -> Unit)? = null
) {
    rememberStableText(value, onValueChange).let { stable ->
        OutlinedTextField(
            value = stable.value,
            onValueChange = stable.onValueChange,
            label = { Text(label) },
            placeholder = { Text(placeholder) },
            leadingIcon = { Icon(icon, contentDescription = null) },
            singleLine = true,
            modifier = Modifier.fillMaxWidth(),
            keyboardOptions = KeyboardOptions(
                capitalization = if (imeAction == ImeAction.Next) KeyboardCapitalization.Words else KeyboardCapitalization.Sentences,
                imeAction = imeAction
            ),
            keyboardActions = KeyboardActions(
                onDone = { onDone?.invoke() }
            ),
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = DarkGold,
                unfocusedBorderColor = Color.White.copy(alpha = 0.3f),
                focusedLabelColor = DarkGold,
                unfocusedLabelColor = Color.White.copy(alpha = 0.7f),
                focusedTextColor = Color.White,
                unfocusedTextColor = Color.White,
                focusedLeadingIconColor = DarkGold,
                unfocusedLeadingIconColor = Color.White.copy(alpha = 0.5f),
                focusedPlaceholderColor = Color.White.copy(alpha = 0.6f),
                unfocusedPlaceholderColor = Color.White.copy(alpha = 0.6f),
                cursorColor = DarkGold
            )
        )
    }
}
